import React, { useState } from 'react';

export default function NotificationTest() {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const subscribeToNotifications = async () => {
    setLoading(true);
    setStatus('Requesting permission...');

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      console.log('Permission:', permission);

      if (permission !== 'granted') {
        setStatus('❌ Permission denied. Please allow notifications in your browser settings.');
        setLoading(false);
        return;
      }

      setStatus('Permission granted! Subscribing...');

      // Wait for service worker
      const registration = await navigator.serviceWorker.ready;
      console.log('Service worker ready');

      // Get VAPID key
      const vapidResponse = await fetch((process.env.REACT_APP_API_URL || '') + '/api/vapid-public-key', {
        credentials: 'include'
      });
      const { publicKey } = await vapidResponse.json();

      // Helper function
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      const arrayBufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send to server
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });

      if (response.ok) {
        setStatus('✅ Successfully subscribed to notifications!');
      } else {
        setStatus('❌ Failed to save subscription to server');
      }
    } catch (error) {
      setStatus('❌ Error: ' + error.message);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setLoading(true);
    setStatus('Sending...');

    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/test-push', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('✅ Test notification sent! Check your notifications.');
        console.log('Success:', data);
      } else {
        setStatus('❌ Failed: ' + (data.error || 'Unknown error'));
        console.error('Error:', data);
      }
    } catch (error) {
      setStatus('❌ Network error: ' + error.message);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    setLoading(true);
    setStatus('Checking...');

    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/push-subscription-status', {
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setStatus(
          `📊 Subscription Status:\n` +
          `- Has subscription: ${data.hasSubscription}\n` +
          `- Count: ${data.subscriptionCount}\n` +
          `- Subscriptions: ${JSON.stringify(data.subscriptions, null, 2)}`
        );
        console.log('Subscription status:', data);
      } else {
        setStatus('❌ Failed to check status');
        console.error('Error:', data);
      }
    } catch (error) {
      setStatus('❌ Error: ' + error.message);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      background: 'white',
      padding: 20,
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 9999,
      maxWidth: 400,
      direction: 'rtl'
    }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>🧪 اختبار التنبيهات</h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          onClick={subscribeToNotifications}
          disabled={loading}
          style={{
            padding: '10px 16px',
            background: '#f59e42',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'جاري الاشتراك...' : '🔔 الاشتراك في التنبيهات'}
        </button>

        <button
          onClick={sendTestNotification}
          disabled={loading}
          style={{
            padding: '10px 16px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'جاري الإرسال...' : '📬 إرسال تنبيه تجريبي'}
        </button>

        <button
          onClick={checkSubscriptionStatus}
          disabled={loading}
          style={{
            padding: '10px 16px',
            background: '#22c55e',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'جاري الفحص...' : '📊 فحص حالة الاشتراك'}
        </button>
      </div>

      {status && (
        <div style={{
          marginTop: 12,
          padding: 10,
          background: '#f8f9fa',
          borderRadius: 6,
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          maxHeight: 200,
          overflow: 'auto',
          textAlign: 'right'
        }}>
          {status}
        </div>
      )}
    </div>
  );
}
